import { useEffect, useState } from "react";
import { NavLink, Outlet, useParams } from "react-router";
import { ApiError } from "../api/client";
import { ConfirmDialog } from "../components/Dialog";
import { useCurrentUser } from "../pages/AuthPages";
import { CampaignProvider, useCampaign } from "./context";
import { QuickBar } from "./QuickBar";

export function CampaignLayout() {
  const { id = "" } = useParams();
  return (
    <CampaignProvider key={id} id={id}>
      {(head, error, epoch) =>
        head ? (
          <CampaignFrame epoch={epoch} />
        ) : error ? (
          <p className="error">{error instanceof ApiError && error.status === 404 ? "Kampagne nicht gefunden." : error.message}</p>
        ) : (
          <p className="muted">Lade …</p>
        )
      }
    </CampaignProvider>
  );
}

function CampaignFrame(props: { epoch: number }) {
  const campaign = useCampaign();
  const { mode, leads, term } = campaign;
  const base = `/k/${campaign.id}`;
  const showsLog = leads && mode !== "player";
  return (
    <div className={`campaign${mode === "dossier" ? " dossier" : ""}${mode === "player" ? " player-view" : ""}${showsLog ? " at-table" : ""}`}>
      <CampaignHeader />
      {/* Keyed like the pages below, so the end of the dossier closes its dialogs with what was typed in them; a key of its own, as siblings must not share one. */}
      {showsLog && <QuickBar key={`quickbar-${mode}-${props.epoch}`} />}
      {campaign.notice && (
        <div className="notice" role="status">
          <p>{campaign.notice}</p>
          <button type="button" className="link" onClick={campaign.clearNotice}>
            Schließen
          </button>
        </div>
      )}
      <nav className="tabs" aria-label="Kampagnenbereiche">
        <NavLink to={base} end>
          Start
        </NavLink>
        {showsLog && <NavLink to={`${base}/abenteuer`}>{term("adventure")}</NavLink>}
        {showsLog && <NavLink to={`${base}/sitzungen`}>{term("session")}</NavLink>}
        <NavLink to={`${base}/eintraege`}>Einträge</NavLink>
        <NavLink to={`${base}/suche`}>Suche</NavLink>
        <NavLink to={`${base}/gruppe`}>{term("party")}</NavLink>
        <NavLink to={`${base}/welt`}>{term("world")}</NavLink>
        {showsLog && <NavLink to={`${base}/protokoll`}>Protokoll</NavLink>}
        {showsLog && <NavLink to={`${base}/einstellungen`}>Einstellungen</NavLink>}
      </nav>
      <div key={`${mode}-${props.epoch}`}>
        <Outlet />
      </div>
    </div>
  );
}

function CampaignHeader() {
  const campaign = useCampaign();
  const { head, mode, leads, term } = campaign;
  const user = useCurrentUser();
  const [confirming, setConfirming] = useState(false);
  const [leavingDrafts, setLeavingDrafts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const roleTerm = term(head.role === "gameMaster" ? "gameMaster" : "player");
  return (
    <header className="campaign-header">
      <div className="campaign-title">
        <h1>{head.name}</h1>
        {mode !== "player" && (
          <p className="muted small">
            {roleTerm} · {head.systemName.de}
          </p>
        )}
        {mode === "player" && leads && <p className="small player-view-label">{term("player")}: so sehen es alle am Tisch</p>}
      </div>
      {mode === "dossier" && <DossierBar />}
      {leads && (
        <div className="campaign-actions">
          {mode !== "player" && (
            <button
              type="button"
              className="icon-button"
              disabled={!head.undo.available}
              title={head.undo.available ? `Rückgängig: ${head.undo.summary}` : (head.undo.hint ?? "Nichts rückgängig zu machen")}
              aria-label="Rückgängig"
              onClick={() => void run(campaign.undo)}
            >
              ↶
            </button>
          )}
          {mode === "normal" && (
            <button type="button" className="button dossier-switch" aria-pressed={false} onClick={() => setConfirming(true)}>
              Dossier
            </button>
          )}
          <button
            type="button"
            className={`button secondary${mode === "player" ? " selected" : ""}`}
            aria-pressed={mode === "player"}
            onClick={() => (campaign.hasDrafts() ? setLeavingDrafts(true) : campaign.setPlayerView(mode !== "player"))}
          >
            {mode === "player" ? "Spieleransicht beenden" : "Spieleransicht"}
          </button>
        </div>
      )}
      {mode !== "player" && !head.undo.available && head.undo.hint && <p className="muted small campaign-hint">{head.undo.hint}</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {confirming && (
        <ConfirmDialog title="Dossier einblenden" action="Dossier einblenden" onClose={() => setConfirming(false)} onConfirm={campaign.openDossier}>
          <p>Das Dossier zeigt alles, auch Geheimes und Künftiges. Niemand außer dir sollte jetzt auf den Bildschirm sehen.</p>
          {campaign.hasDrafts() && <p className="error">Nicht gespeicherte Eingaben auf dieser Seite gehen dabei verloren.</p>}
          <p className="muted small">
            Es gilt nur in diesem Tab und endet nach {user.dossierMinutes} Minuten ohne Eingabe, beim Verlassen der Kampagne oder mit „Ausblenden“.
          </p>
        </ConfirmDialog>
      )}
      {leavingDrafts && (
        <ConfirmDialog
          title={mode === "player" ? "Spieleransicht beenden" : "Spieleransicht"}
          action="Trotzdem wechseln"
          onClose={() => setLeavingDrafts(false)}
          onConfirm={async () => campaign.setPlayerView(mode !== "player")}
        >
          <p>Nicht gespeicherte Eingaben auf dieser Seite gehen beim Wechsel der Ansicht verloren.</p>
        </ConfirmDialog>
      )}
    </header>
  );
}

function DossierBar() {
  const campaign = useCampaign();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const left = Math.max(0, Math.ceil(((campaign.dossierDeadline ?? now) - now) / 1000));
  const minutes = Math.floor(left / 60);
  const seconds = String(left % 60).padStart(2, "0");
  return (
    <div className="dossier-bar" role="status" aria-label="Dossier">
      <strong>Dossier</strong>
      <span className="dossier-time" title="Restzeit ohne Eingabe">
        noch {minutes}:{seconds}
      </span>
      <button type="button" className="button danger" onClick={() => campaign.closeDossier()}>
        Ausblenden
      </button>
    </div>
  );
}
